import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Sparkles, Utensils, Radio, MoreHorizontal, User, Video, Settings, Brain, Crown, ShoppingCart, BookOpen, Users, Trophy, Award, HelpCircle, Smartphone, Telescope, ShoppingBag, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';

interface NavItem {
  path: string;
  icon: typeof Home;
  labelKey: string;
}

const navItems: NavItem[] = [
  { path: '/', icon: Home, labelKey: 'nav.home' },
  { path: '/chaos', icon: Sparkles, labelKey: 'nav.aiEngine' },
  { path: '/recipes', icon: Utensils, labelKey: 'nav.cookAndPlan' },
  { path: '/live', icon: Radio, labelKey: 'nav.live' },
];

interface MoreLink {
  path: string;
  icon: typeof Home;
  labelKey: string;
  requiresAuth?: boolean;
}

interface MoreSection {
  titleKey: string;
  links: MoreLink[];
}

const moreSections: MoreSection[] = [
  {
    titleKey: 'nav.moreAccount',
    links: [
      { path: '/profile', icon: User, labelKey: 'nav.profile', requiresAuth: true },
      { path: '/creator/dashboard', icon: Video, labelKey: 'nav.creatorDashboard', requiresAuth: true },
      { path: '/settings/subscription', icon: Settings, labelKey: 'nav.settings', requiresAuth: true },
    ],
  },
  {
    titleKey: 'nav.moreTools',
    links: [
      { path: '/ai-tools', icon: Brain, labelKey: 'nav.aiTools' },
      { path: '/pantry', icon: Package, labelKey: 'nav.pantryTracker' },
      { path: '/pricing', icon: Crown, labelKey: 'nav.pricing' },
      { path: '/credits', icon: ShoppingCart, labelKey: 'nav.credits' },
    ],
  },
  {
    titleKey: 'nav.moreDiscover',
    links: [
      { path: '/recipe-book', icon: BookOpen, labelKey: 'nav.recipeBook' },
      { path: '/community', icon: Users, labelKey: 'nav.community' },
      { path: '/challenges', icon: Trophy, labelKey: 'nav.challenges' },
      { path: '/leaderboard', icon: Award, labelKey: 'nav.leaderboard' },
      { path: '/merch', icon: ShoppingBag, labelKey: 'nav.merch' },
      { path: '/streak', icon: Trophy, labelKey: 'nav.streakHistory' },
    ],
  },
  {
    titleKey: 'nav.moreHelp',
    links: [
      { path: '/support', icon: HelpCircle, labelKey: 'nav.helpSupport' },
      { path: '/download', icon: Smartphone, labelKey: 'nav.getApp' },
      { path: '/roadmap', icon: Telescope, labelKey: 'nav.roadmap' },
    ],
  },
];

export const MobileBottomNav = React.memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isLoggedIn } = useCurrentUser();
  const [moreOpen, setMoreOpen] = useState(false);

  if (location.pathname === '/auth' || location.pathname.endsWith('/print')) {
    return null;
  }

  return (
    <>
      <nav
        aria-label={t("nav.mainNavigation", { defaultValue: "Main navigation" })}
        className="fixed bottom-0 left-0 right-0 z-50 bg-card/70 backdrop-blur-xl border-t border-border/50 md:hidden no-print"
        data-print-hide="true"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxShadow: '0 -1px 12px hsl(var(--background) / 0.5)',
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="flex items-center justify-around h-[4.5rem] px-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));

            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={isActive ? "page" : undefined}
                aria-label={t(item.labelKey)}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 min-w-[56px] min-h-[44px] px-2 py-1.5 rounded-xl transition-all duration-200 tap-highlight-none',
                  'active:scale-95',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <div className={cn(
                  'flex items-center justify-center w-10 h-8 rounded-xl transition-all duration-200',
                  isActive && 'bg-primary/15 scale-110'
                )}>
                  <item.icon className={cn('w-5 h-5 transition-colors', isActive && 'text-primary')} />
                </div>
                <span className={cn(
                  'text-[10px] font-medium truncate max-w-[56px] transition-colors',
                  isActive && 'text-primary font-semibold'
                )}>
                  {t(item.labelKey)}
                </span>
                {isActive && (
                  <div className="absolute bottom-1.5 w-1 h-1 rounded-full bg-primary" style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }} />
                )}
              </Link>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            aria-label={t('nav.more')}
            className={cn(
              'flex flex-col items-center justify-center gap-1 min-w-[56px] min-h-[44px] px-2 py-1.5 rounded-xl transition-all duration-200 tap-highlight-none',
              'active:scale-95',
              moreOpen
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <div className={cn(
              'flex items-center justify-center w-10 h-8 rounded-xl transition-all duration-200',
              moreOpen && 'bg-primary/15 scale-110'
            )}>
              <MoreHorizontal className={cn('w-5 h-5 transition-colors', moreOpen && 'text-primary')} />
            </div>
            <span className={cn(
              'text-[10px] font-medium truncate max-w-[56px] transition-colors',
              moreOpen && 'text-primary font-semibold'
            )}>
              {t('nav.more')}
            </span>
          </button>
        </div>
      </nav>

      {/* More Sheet */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-8 max-h-[80vh] overflow-y-auto">
          <SheetTitle className="sr-only">{t('nav.more', { defaultValue: 'More' })}</SheetTitle>
          <div className="mx-auto w-10 h-1 rounded-full bg-muted mb-4 mt-1" />

          <div className="space-y-5">
            {moreSections.map((section) => (
              <div key={section.titleKey}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  {t(section.titleKey)}
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {section.links.map((link) => {
                    const path = link.requiresAuth && !isLoggedIn ? '/auth' : link.path;
                    const isActive = location.pathname === link.path;

                    return (
                      <Link
                        key={link.path}
                        to={path}
                        onClick={() => setMoreOpen(false)}
                        className={cn(
                          'flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors',
                          'active:scale-95 tap-highlight-none',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted/50 text-foreground hover:bg-muted'
                        )}
                      >
                        <link.icon className="w-5 h-5" />
                        <span className="text-[11px] font-medium text-center leading-tight">
                          {t(link.labelKey)}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
});
